import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../const/i18nNamespaces';
import React, {useEffect, useMemo, useState} from 'react';
import {useFormik} from 'formik';
import {Button, Grid, TextField} from '@material-ui/core';
import useStyles from './NewPrescriptionDraftFormStyles';
import prescriptionsApi from '../../../api/prescriptions/prescriptionsApi';
import {
    MedicationPackDto,
    MedicinalProductDto,
    PrescriptionDraft,
} from '../../../types/prescription';
import * as yup from 'yup';
import MedicinalProductAutocomplete from '../MedicinalProductAutocomplete/MedicinalProductAutocomplete';
import MedicationPackSelect from '../MedicationPackSelect/MedicationPackSelect';

type NewPrescriptionDraftFormProps = {
    onSubmit: (prescription: PrescriptionDraft) => void;
};

const REQUIRED_STRING = 'requiredField';
const MIN_ONE = 'minOne';

const NewPrescriptionDraftForm = ({
    onSubmit,
}: NewPrescriptionDraftFormProps) => {
    const {t} = useTranslation(i18nNamespaces.PRESCRIPTIONS);
    const {t: commonT} = useTranslation(i18nNamespaces.COMMON);
    const classes = useStyles();

    const [medicationPacks, setMedicationPacks] = useState<MedicationPackDto[]>(
        [],
    );

    const formik = useFormik({
        initialValues: {
            medicinalProduct: null as MedicinalProductDto,
            medicationPackId: '' as number | string,
            quantity: 1,
        },
        validationSchema: yup.object({
            medicinalProduct: yup
                .object()
                .nullable()
                .required(commonT(REQUIRED_STRING)),
            medicationPackId: yup
                .number()
                .nullable()
                .required(commonT(REQUIRED_STRING)),
            quantity: yup
                .number()
                .required(commonT(REQUIRED_STRING))
                .min(1, commonT(MIN_ONE)),
        }),
        onSubmit: values => {
            if (!values.medicinalProduct || !values.medicationPackId) return;
            onSubmit({
                medicinalProduct: values.medicinalProduct,
                medicationPack: medicationPacks.find(
                    pack => pack.id === values.medicationPackId,
                ),
                quantity: values.quantity
            });
            formik.setFieldValue('medicinalProduct', '');
            formik.resetForm();
        },
    });

    const medicationPackReimbursements = useMemo(
        () =>
            medicationPacks?.find(
                pack => pack.id === formik.values?.medicationPackId,
            )?.medicationPackReimbursements,
        [medicationPacks, formik.values?.medicationPackId],
    );

    useEffect(() => {
        if (!formik.values.medicinalProduct) return undefined;
        (async () => {
            try {
                const {data} = await prescriptionsApi.getMedicationPackList(
                    formik.values.medicinalProduct?.id,
                );
                setMedicationPacks(data.result);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [formik.values.medicinalProduct]);

    const handleChangeMedicationPack = (
        event: React.ChangeEvent<{
            name?: string;
            value: unknown;
        }>,
    ) => {
        formik.setFieldValue('medicationPackId', event.target.value);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2} className={classes.container}>
                <Grid item xs={12}>
                    <MedicinalProductAutocomplete
                        onChange={newValue =>
                            formik.setFieldValue('medicinalProduct', newValue)
                        }
                        medicinalProduct={formik.values.medicinalProduct}
                        error={
                            formik.touched.medicinalProduct &&
                            Boolean(formik.errors.medicinalProduct)
                        }
                        helperText={
                            formik.touched.medicinalProduct &&
                            formik.errors.medicinalProduct
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <MedicationPackSelect
                        medicationPacks={medicationPacks}
                        medicationPackId={formik.values.medicationPackId}
                        onChange={handleChangeMedicationPack}
                        error={Boolean(
                            formik.touched.medicationPackId &&
                                formik.errors.medicationPackId,
                        )}
                        helperText={formik.errors.medicationPackId}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        type="number"
                        label={t('quantity')}
                        variant="outlined"
                        name="quantity"
                        value={formik.values.quantity}
                        onChange={formik.handleChange}
                        InputProps={{inputProps: {min: 1}}}
                        helperText={
                            formik.touched.quantity && formik.errors.quantity
                        }
                        error={Boolean(
                            formik.touched.quantity && formik.errors.quantity,
                        )}
                    />
                </Grid>
                <Grid item xs={12} style={{textAlign: 'right'}}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        style={{fontWeight: 'bold', padding: '0 25px', width: 200}}
                    >
                        {t('add')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

export default NewPrescriptionDraftForm;
